/** @format */

import { create } from "zustand";
import { safeStorage } from "@/utils/storage";
import { getVisitCount } from "@/utils/visitCounter";

export type CTAId = "sign-up" | "pwa-install" | "notifications" | "favorite-drivers";

/** Lower index = higher priority */
const CTA_PRIORITY: CTAId[] = [
  "sign-up",
  "pwa-install",
  "notifications",
  "favorite-drivers",
];

/** Number of visits required after a CTA dismissal before showing the next */
const VISIT_COOLDOWN = 3;

/** localStorage key for the visit count at which the last CTA was dismissed */
const DISMISSED_AT_KEY = "cta-dismissed-at-visit";

function getDismissedAtVisit(): number | null {
  const raw = safeStorage.getItem(DISMISSED_AT_KEY);
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function isInVisitCooldown(): boolean {
  const dismissedAt = getDismissedAtVisit();
  if (dismissedAt === null) return false;
  return getVisitCount() < dismissedAt + VISIT_COOLDOWN;
}

function computeActiveCTA(eligible: Set<CTAId>): CTAId | null {
  if (isInVisitCooldown()) return null;
  for (const id of CTA_PRIORITY) {
    if (eligible.has(id)) return id;
  }
  return null;
}

interface CTAState {
  eligible: Set<CTAId>;
  activeCTA: CTAId | null;

  register: (id: CTAId) => void;
  unregister: (id: CTAId) => void;
  dismiss: (id: CTAId) => void;
  /** Re-evaluate active CTA (call after visit count changes) */
  recompute: () => void;
}

export const useCTAStore = create<CTAState>((set, get) => ({
  eligible: new Set<CTAId>(),
  activeCTA: null,

  register: (id: CTAId) => {
    const { eligible } = get();
    if (eligible.has(id)) return;
    const next = new Set(eligible);
    next.add(id);
    set({ eligible: next, activeCTA: computeActiveCTA(next) });
  },

  unregister: (id: CTAId) => {
    const { eligible } = get();
    if (!eligible.has(id)) return;
    const next = new Set(eligible);
    next.delete(id);
    set({ eligible: next, activeCTA: computeActiveCTA(next) });
  },

  dismiss: (id: CTAId) => {
    const { eligible } = get();
    const next = new Set(eligible);
    next.delete(id);
    safeStorage.setItem(DISMISSED_AT_KEY, String(getVisitCount()));
    set({ eligible: next, activeCTA: null });
  },

  recompute: () => {
    const { eligible } = get();
    set({ activeCTA: computeActiveCTA(eligible) });
  },
}));
